import { makeStyles } from '@material-ui/core/styles';
import { useState } from 'react';
import { Store, useAppDispatch } from 'src/redux/Store';
import { useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import { t } from 'src/lib/language';
import SubmenuLink from 'src/components/UI/SubmenuLink';
import { closeModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { FundraisingTypes } from 'src/constants/FundraisingTypes';
import FundraisingInformationForm
  from 'src/components/Forms/Fundraising/FundraisingInformationForm';
import {
  FundraisingInfo, FundraisingModel, Step, TABS,
} from 'src/models/FundraisingModel';
import FundraisingProductdForm from 'src/components/Forms/Fundraising/FundraisingProductsForm';
import moment from 'moment-timezone-all';
import { openDialog } from 'src/redux/actions/dialogActions';
import { DIALOGS } from 'src/models/DialogModel';
import palette from 'src/styles/palette';
import Title from 'src/components/UI/Title';
import FundraisingAmbassadorsForm
  from 'src/components/Forms/Fundraising/FundraisingAmbassadorsForm';
import FundraisingNotificationForm
  from 'src/components/Forms/Fundraising/FundraisingNotificationForm';
import FundraisingAssetsForm from 'src/components/Forms/Fundraising/FundraisingAssetsForm';
import {
  postFundraisingInfoAction,
  putFundraisingAmbassadorsAction,
  putFundraisingAssetsAction,
  putFundraisingInfoAction,
  putFundraisingNotificationsAction,
  putFundraisingProductsAction,
} from 'src/redux/actions/fundraisingAction';
import { FundraisingLanguages } from 'src/constants/FundraisingLanguages';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    maxWidth: '640px',
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  formControl: {
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  selectElement: {
    width: '100%',
  },
  selectLabel: {
    color: 'rgba(0, 0, 0, 0.50)',
  },
  selectLabelError: {
    color: 'rgba(255, 0, 0, 0.50)',
  },
  spacer: {
    height: theme.spacing(1),
  },
  hide: {
    display: 'none',
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'top',
    gap: '10px',
    marginTop: '10px',
  },
  header: {
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1),
    height: '54px',
  },
  title: {
    color: palette.primary.contrastText,
    backgroundColor: palette.primary.light,
    padding: '8px',
    fontSize: '18px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  scroller: {
    overflowY: 'auto',
    height: '66vh',
  },
}));

export interface FundraisingProps {
  onChange: (fundraisingState: FundraisingModel, step: number, advance: boolean) => void;
  setErrors: (tab:TABS, hasError: boolean) => void;
  setHasChanged: (hasChanged: boolean) => void;
  fundraisingState: FundraisingModel;
  hasChanged?: boolean;
}

type ErrorState = {
  [key in TABS]: boolean
};

export const renderHasChanged = (hasChanged: boolean) => {
  if (!hasChanged) {
    return (<><sup>*</sup><span>&nbsp;</span></>);
  }
  return null;
};

const AddFundraisingPage = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const lang = useSelector((state: Store) => state.language.language);

  const [currentTab, setCurrentTab] = useState(TABS.information);
  const [step, setStep] = useState(1);
  const [hasErrors, setHasErrors] = useState({} as ErrorState);
  const [hasChanged, setHasChanged] = useState(false);

  const [fundraisingState, setFundraisingState] = useState({
    fundraisingInfo: {
      active: true,
      show_countdown: false,
      type: FundraisingTypes.standard,
      default_language: FundraisingLanguages.en,
      start_date: moment().add(1, 'day').startOf('day'),
      end_date: moment().add(1, 'week').endOf('day'),
    } as FundraisingInfo,
    matchingInfo: null,
    products: null,
    ambassadors: null,
    assets: null,
  } as FundraisingModel);

  const onNextStep = (newStep: number) => {
    if (newStep > step) {
      setStep(newStep);
    }
    switch (newStep) {
      case 2:
        setCurrentTab(TABS.products);
        break;
      case 3:
        setCurrentTab(TABS.assets);
        break;
      case 4:
        setCurrentTab(TABS.ambassador);
        break;
      case 5:
        setCurrentTab(TABS.notifications);
        break;
      default:
        break;
    }
  };

  const onChangeCallback = (state: FundraisingModel, advance: boolean) => {
    setFundraisingState(state);
    if (advance) {
      onNextStep(Math.min(step + 1, 5));
    }
  };

  const onChange = (state: FundraisingModel, currentStep: Step, advance: boolean) => {
    const fundraisingId = state?.fundraisingInfo?.id;
    if (currentStep === Step.info && !fundraisingId) {
      dispatch(
        postFundraisingInfoAction(
          state,
          (newState: FundraisingModel) => onChangeCallback(newState, advance),
        ),
      );
      return;
    }
    if (!fundraisingId) { return; }
    switch (currentStep) {
      case Step.info:
        dispatch(
          putFundraisingInfoAction(
            fundraisingId,
            state,
            (newState: FundraisingModel) => onChangeCallback(newState, advance),
          ),
        );
        break;
      case Step.products:
        dispatch(
          putFundraisingProductsAction(
            fundraisingId,
            state,
            (newState: FundraisingModel) => onChangeCallback(newState, advance),
          ),
        );
        break;
      case Step.ambassadors:
        dispatch(
          putFundraisingAmbassadorsAction(
            fundraisingId,
            state,
            (newState: FundraisingModel) => onChangeCallback(newState, advance),
          ),
        );
        break;
      case Step.notifications:
        dispatch(
          putFundraisingNotificationsAction(
            fundraisingId,
            state,
            (newState: FundraisingModel) => onChangeCallback(newState, advance),
          ),
        );
        break;
      case Step.assets:
        dispatch(
          putFundraisingAssetsAction(
            fundraisingId,
            state,
            (newState: FundraisingModel) => onChangeCallback(newState, advance),
          ),
        );
        break;
      default:
    }
  };

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.addEvent }));
    setStep(1);
  };

  const setTabError = (tab: TABS, hasError: boolean) => {
    setHasErrors({
      ...hasErrors,
      [tab]: hasError,
    });
  };

  const changeTabCallback = (tab: TABS) => {
    setHasChanged(false);
    setCurrentTab(tab);
  };

  const changeTab = (tab: TABS) => {
    if (hasChanged) {
      dispatch(openDialog(
        { dialog: DIALOGS.leaveTab, payload: { callback: () => changeTabCallback(tab) } },
      ));
      return;
    }
    setCurrentTab(tab);
  };

  const renderForm = () => {
    switch (currentTab) {
      case TABS.information:
        return (
          <FundraisingInformationForm
            onChange={onChange}
            setErrors={setTabError}
            setHasChanged={setHasChanged}
            hasChanged={hasChanged}
            fundraisingState={fundraisingState} />
        );
      case TABS.products:
        return (
          <FundraisingProductdForm
            onChange={onChange}
            setErrors={setTabError}
            fundraisingState={fundraisingState}
            setHasChanged={setHasChanged}
            hasChanged={hasChanged} />
        );
      case TABS.assets:
        return (
          <FundraisingAssetsForm
            onChange={onChange}
            setErrors={setTabError}
            setHasChanged={setHasChanged}
            hasChanged={hasChanged}
            fundraisingState={fundraisingState} />
        );
      case TABS.ambassador:
        return (
          <FundraisingAmbassadorsForm
            onChange={onChange}
            setErrors={setTabError}
            setHasChanged={setHasChanged}
            hasChanged={hasChanged}
            fundraisingState={fundraisingState} />
        );
      case TABS.notifications:
        return (
          <FundraisingNotificationForm
            onChange={onChange}
            setHasChanged={setHasChanged}
            hasChanged={hasChanged}
            setErrors={setTabError}
            fundraisingState={fundraisingState} />
        );
      default:
        return (
          <FundraisingInformationForm
            onChange={onChange}
            setErrors={setTabError}
            setHasChanged={setHasChanged}
            hasChanged={hasChanged}
            fundraisingState={fundraisingState} />
        );
    }
  };

  const hasSlug = () => !!fundraisingState?.fundraisingInfo?.slug;
  const hasId = () => !!fundraisingState?.fundraisingInfo?.id;
  const fundraisingType = fundraisingState?.fundraisingInfo?.type;
  return (
    <>
      <div className={classes.header}>
        <div className={classes.row}>
          <Title>Add a fundraising</Title>
          <Button
            style={{ marginLeft: 'auto', marginBottom: '0px' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </div>
      </div>
      <div className={classes.row}>
        <SubmenuLink
          onClick={() => changeTab(TABS.information)}
          selected={currentTab === TABS.information}
          hasError={hasErrors[TABS.information]}
          color="#0A4DF2">
          Informations
        </SubmenuLink>
        <SubmenuLink
          onClick={() => changeTab(TABS.products)}
          selected={currentTab === TABS.products}
          disabled={!hasId() || !hasSlug()}
          color="#0A4DF2"
          hasError={hasErrors[TABS.products]}>
          Products
        </SubmenuLink>
        <SubmenuLink
          onClick={() => changeTab(TABS.assets)}
          selected={currentTab === TABS.assets}
          disabled={!hasId() || !hasSlug()}
          color="#0A4DF2"
          hasError={hasErrors[TABS.assets]}>
          Assets
        </SubmenuLink>
        {fundraisingType === FundraisingTypes.matching && (
          <SubmenuLink
            onClick={() => changeTab(TABS.ambassador)}
            selected={currentTab === TABS.ambassador}
            disabled={!hasId() || !hasSlug()}
            color="#0A4DF2"
            hasError={hasErrors[TABS.ambassador]}>
            Ambassador
          </SubmenuLink>
        )}
        <SubmenuLink
          onClick={() => changeTab(TABS.notifications)}
          selected={currentTab === TABS.notifications}
          disabled={!hasId() || !hasSlug()}
          color="#0A4DF2"
          hasError={hasErrors[TABS.notifications]}>
          Notifications/Social
        </SubmenuLink>
      </div>
      {renderForm()}
    </>
  );
};

export default AddFundraisingPage;
